/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { formatPrice } from "@lemmonhq/orders";
import { FastNumberInput, useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";

interface CustomDiscountFormProps {
  selectedTotal: number;
  selectedQuantity: number;
  disabled: boolean;
  onApply: (valueType: "percentage" | "amount", value: number) => void;
}

export const CustomDiscountForm = defineComponent<CustomDiscountFormProps>({
  name: "CustomDiscountForm",
  props: ["selectedTotal", "selectedQuantity", "disabled", "onApply"],
  setup(props) {
    const auth = useAuth();
    const currency = computed(
      () => auth.user.value?.restaurant.currency || "CHF",
    );
    const { t } = useLocalization();
    const valueType = ref<"percentage" | "amount">("percentage");
    const value = ref<number | null>(null);

    const discountAmount = computed(() => {
      if (value.value == null || value.value <= 0) return null;
      if (valueType.value === "percentage") {
        if (value.value > 100) return null;
        return (props.selectedTotal * value.value) / 100;
      }
      return Math.min(
        value.value * props.selectedQuantity,
        props.selectedTotal,
      );
    });

    const previewText = computed(() => {
      if (discountAmount.value == null || props.selectedTotal <= 0) return null;
      const remaining = props.selectedTotal - discountAmount.value;
      return `${formatPrice(props.selectedTotal, true, 2, currency.value)} → ${formatPrice(remaining, true, 2, currency.value)}`;
    });

    const canApply = computed(
      () => !props.disabled && discountAmount.value != null,
    );

    const handleApply = () => {
      if (!canApply.value || value.value == null) return;
      props.onApply(valueType.value, value.value);
      value.value = null;
    };

    return () => (
      <div class="space-y-4">
        {/* Type toggle */}
        <div class="flex rounded rounded-lg border border-base-content/10 p-0.5 gap-0.5 bg-base-200 l-box-shadow">
          {(
            [
              {
                label: t("pos.product_discount.type_percentage"),
                val: "percentage",
              },
              { label: t("pos.product_discount.type_fixed"), val: "amount" },
            ] as const
          ).map(({ label, val }) => (
            <button
              key={val}
              type="button"
              class={`flex-1 py-2 text-xs font-semibold transition-all ${
                valueType.value === val
                  ? "bg-base-100 text-accent shadow-sm"
                  : "text-base-content/50 hover:text-base-content/80"
              }`}
              onClick={() => {
                valueType.value = val;
                value.value = null;
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Number input */}
        <FastNumberInput
          value={value.value}
          onChange={(v) => (value.value = v)}
          min={1}
          simplified
          simplifiedLabel={
            valueType.value === "percentage"
              ? t("pos.product_discount.label_percentage")
              : t("pos.product_discount.label_amount")
          }
          simplifiedHelperText={
            valueType.value === "percentage"
              ? t("pos.product_discount.helper_percentage")
              : t("pos.product_discount.helper_amount")
          }
        />

        {/* Preview */}
        {previewText.value && (
          <div class="flex items-center justify-between rounded-lg bg-success/8 border border-success/20 px-3 py-2">
            <span class="text-xs text-success/80">
              {valueType.value === "percentage"
                ? t("pos.product_discount.preview_percentage").replace(
                    "{0}",
                    String(value.value ?? ""),
                  )
                : t("pos.product_discount.preview_amount").replace(
                    "{0}",
                    String(value.value ?? ""),
                  )}
            </span>
            <span class="text-xs font-semibold text-success">
              {previewText.value}
            </span>
          </div>
        )}

        {/* Apply button */}
        <button
          type="button"
          class="btn btn-primary w-full rounded-xl"
          disabled={!canApply.value}
          onClick={handleApply}
        >
          {t("pos.product_discount.apply_discount")}
          {discountAmount.value != null && (
            <span class="ml-1">
              ({formatPrice(discountAmount.value, true, 2, currency.value)})
            </span>
          )}
        </button>
      </div>
    );
  },
});
